import { Inspector } from './Inspector';
import { InspectorConsole } from './Inspector/InspectorConsole';
import { Interpreter } from './Interpreter';
import { StateMachine } from './StateMachine';
import { CreateMachineOptions, MachineDefinition } from './types';
import { isDevelopment } from './util';
import { ensureFsmDevToolsBridge, registerMachine } from './devtools';

function captureCallerFile(): string | undefined {
  try {
    const lines = (new Error().stack ?? '').split('\n');
    for (const line of lines) {
      const match = line.match(/\(?((?:https?:|webpack:|bpm:)\/\/[^\s)]+)\)?/);
      if (!match) continue;
      const url = match[1].replace(/:\d+(?::\d+)?$/, '');
      if (
        /\/fsm\/static/.test(url) ||
        /webpack:\/\/fsm\//.test(url) ||
        /(?:^|\/)fsm@[^/]+\/js\//.test(url) ||
        /\/node_modules\//.test(url) ||
        /[/.]bpm\//.test(url)
      ) {
        continue;
      }
      const fileName =
        url.split(/[?#]/)[0].split('/').filter(Boolean).pop() ?? '';
      if (fileName) return fileName;
    }
  } catch {
    // best-effort — ignore errors
  }
  return undefined;
}

function buildDefaultInspector<TEvent extends { type: string }>(
  name?: string,
  sourceFile?: string
) {
  return new Inspector<TEvent>({
    initialStatus: 'running',
    name,
    sourceFile,
  });
}

export function createMachine<
  TContext = any,
  TEvent extends { type: string } = any,
  TState extends string = string,
  TAction extends string = string,
  TGuard extends string = string,
  TService extends string = string,
>(
  machineDefinition: MachineDefinition<
    TContext,
    TEvent,
    TState,
    TAction,
    TGuard,
    TService
  >,
  options: CreateMachineOptions<TContext, TEvent> = {}
) {
  const inspector =
    options.inspector ||
    (options.debug
      ? new InspectorConsole<TEvent>({
          name: options.name || machineDefinition.initial,
        })
      : buildDefaultInspector<TEvent>(
          options.name,
          isDevelopment() ? captureCallerFile() : undefined
        ));
  const machine = new StateMachine<TContext, TEvent>(
    machineDefinition,
    inspector,
    options.debug || false
  );
  const interpreter = new Interpreter<TContext, TEvent>(
    machine,
    options.initialContext
  );

  if (!options.waitToStart) {
    interpreter.start();
  }

  if (isDevelopment()) {
    registerMachine(interpreter);
    ensureFsmDevToolsBridge();
  }

  return interpreter;
}
